import Scene from "~/scene";
import SceneCoords from "~/scene/common/coords";

/* eslint-disable no-magic-numbers */
const TOLERANCE = 0.000001;

export interface IPointerPosition {
	x: number;
	y: number;
}

export interface IPointerArea {
	width: number;
	height: number;
}

export interface IPointerLine {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

interface ICountPosition {
	hudElems: Array<HTMLElement>;
	areaSize: IPointerArea;
	point: IPointerPosition;
	snapHUDtoScreen?: number;
}

// area [x0, y0, x1, y1]
export function isPointInArea(point: IPointerPosition, area: IPointerArea) {
	return point.x >= 0
		&& point.x <= area.width
		&& point.y >= 0
		&& point.y <= area.height;
}

export function getAreaCenter(areaSize: IPointerArea): IPointerPosition {
	return {
		x: Math.floor(areaSize.width / 2),
		y: Math.floor(areaSize.height / 2),
	};
}

export function det2(x1: number, x2: number, y1: number, y2: number) {
	return (x1 * y2) - (y1 * x2);
}

export function lineIntersection(firstLine: IPointerLine, secondLine: IPointerLine) {
	const testDet = det2(firstLine.x1 - firstLine.x2, firstLine.y1 - firstLine.y2, secondLine.x1 - secondLine.x2, secondLine.y1 - secondLine.y2);

	// primky jsou paralelni
	if (Math.abs(testDet) < TOLERANCE) {
		return null;
	}

	const d1 = det2(firstLine.x1, firstLine.y1, firstLine.x2, firstLine.y2);
	const d2 = det2(secondLine.x1, secondLine.y1, secondLine.x2, secondLine.y2);
	const xVal = det2(d1, firstLine.x1 - firstLine.x2, d2, secondLine.x1 - secondLine.x2) / testDet;
	const yVal = det2(d1, firstLine.y1 - firstLine.y2, d2, secondLine.y1 - secondLine.y2) / testDet;

	if (
		(xVal < Math.min(firstLine.x1, firstLine.x2) - TOLERANCE || xVal > Math.max(firstLine.x1, firstLine.x2) + TOLERANCE)
		|| (yVal < Math.min(firstLine.y1, firstLine.y2) - TOLERANCE || yVal > Math.max(firstLine.y1, firstLine.y2) + TOLERANCE)
		|| (xVal < Math.min(secondLine.x1, secondLine.x2) - TOLERANCE || xVal > Math.max(secondLine.x1, secondLine.x2) + TOLERANCE)
		|| (yVal < Math.min(secondLine.y1, secondLine.y2) - TOLERANCE || yVal > Math.max(secondLine.y1, secondLine.y2) + TOLERANCE)
	) {
		return null;
	}

	return {
		x: Math.round(xVal),
		y: Math.round(yVal),
	};
}

export function getLines(hudElems: Array<HTMLElement>, areaSize: IPointerArea, snapHUDtoScreen = 0) {
	// obrazovka
	const lines: Array<IPointerLine> = [
		{ x1: 0, y1: 0, x2: areaSize.width, y2: 0 },
		{ x1: areaSize.width, y1: 0, x2: areaSize.width, y2: areaSize.height },
		{ x1: areaSize.width, y1: areaSize.height, x2: 0, y2: areaSize.height },
		{ x1: 0, y1: areaSize.height, x2: 0, y2: 0 },
	];
	const screenCenter = getAreaCenter(areaSize);

	// hud prvky
	hudElems.forEach(elem => {
		const style = getComputedStyle(elem);
		const display = style.display;
		const visibility = style.visibility;

		if (!(display === "none" || visibility === "hidden")) {
			// preskocime svg
			if (elem.tagName.toLowerCase() !== "svg") {
				let height = elem.offsetHeight;

				if (elem.offsetWidth && !height) {
					const count = elem.children.length;

					for (let ind = 0; ind < count; ind++) {
						height = Math.max(height, (elem.children[ind] as HTMLElement).offsetHeight);
					}
				}

				const bbox = [0, 0, 0, 0];

				if (elem.offsetLeft < screenCenter.x) {
					const left = elem.offsetLeft;

					bbox[0] = left;
					bbox[2] = bbox[0] + elem.offsetWidth;
				} else if (elem.offsetLeft >= screenCenter.x) {
					const right = areaSize.width - elem.offsetLeft - elem.offsetWidth;

					bbox[0] = areaSize.width - right - elem.offsetWidth;
					bbox[2] = bbox[0] + elem.offsetWidth;
				}

				if (elem.offsetTop < screenCenter.y) {
					const top = elem.offsetTop;

					bbox[1] = top;
					bbox[3] = bbox[1] + height;
				} else if (elem.offsetTop >= screenCenter.y) {
					// position top vs bottom
					const bottom = style.bottom.indexOf("px") === -1
						? areaSize.height - elem.offsetTop - elem.offsetHeight
						: 0;

					bbox[1] = areaSize.height - bottom - height;
					bbox[3] = bbox[1] + height;
				}

				// snapovani k okrajum obrazovky
				if (snapHUDtoScreen > 0) {
					// x
					if (bbox[0] <= snapHUDtoScreen) {
						bbox[0] = 0;
					}

					if (areaSize.width - bbox[2] <= snapHUDtoScreen) {
						bbox[2] = areaSize.width;
					}

					// y
					if (bbox[1] <= snapHUDtoScreen) {
						bbox[1] = 0;
					}

					if (areaSize.height - bbox[3] <= snapHUDtoScreen) {
						bbox[3] = areaSize.height;
					}
				}

				// vlozime jednotlive cary bboxu
				lines.push({ x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[1] });
				lines.push({ x1: bbox[2], y1: bbox[1], x2: bbox[2], y2: bbox[3] });
				lines.push({ x1: bbox[2], y1: bbox[3], x2: bbox[0], y2: bbox[3] });
				lines.push({ x1: bbox[0], y1: bbox[3], x2: bbox[0], y2: bbox[1] });
			}
		}
	});

	return lines;
}

export function countPosition({
	hudElems,
	areaSize,
	point,
	snapHUDtoScreen = 0,
}: ICountPosition): null | IPointerPosition {
	let intersection = false;
	const screenCenter = getAreaCenter(areaSize);
	let xAxe = 0;
	let yAxe = 0;

	// primka stredu a souradnice pointeru
	const pointerLine: IPointerLine = {
		x1: screenCenter.x,
		y1: screenCenter.y,
		x2: point.x,
		y2: point.y,
	};

	// testovaci primky
	const lines = getLines(hudElems, areaSize, snapHUDtoScreen);

	// test na prusecik
	let distance = Infinity;

	lines.forEach(line => {
		const lpi = lineIntersection(pointerLine, line);

		if (lpi) {
			const newDistance = Math.round(Math.sqrt(Math.pow(lpi.x - screenCenter.x, 2) + Math.pow(lpi.y - screenCenter.y, 2)));

			if (newDistance < distance) {
				xAxe = lpi.x;
				yAxe = lpi.y;
				intersection = true;
				distance = newDistance;
			}
		}
	});

	if (intersection) {
		return {
			x: xAxe,
			y: yAxe,
		};
	}

	return null;
}

export function movePointByAngle(point: IPointerPosition, angle: number) {
	const rad = angle / 180 * Math.PI;

	return {
		x: (point.x * Math.cos(rad)) - (point.y * Math.sin(rad)),
		y: (point.x * Math.sin(rad)) + (point.y * Math.cos(rad)),
	};
}

export function getPointerDistance(coord1: SceneCoords, coord2: SceneCoords) {
	const distance = coord1.distance(coord2);
	let caption = "";

	// prepocet km, m
	if (distance > 1000) {
		caption = `${(distance / 1000).toFixed(1)} km`;
	} else {
		caption = `${distance >>> 0} m`;
	}

	return caption;
}

export function getPointerCenterPx(coord: SceneCoords): IPointerPosition {
	const centerPx = Scene.coordsToPixel(coord);
	const sceneSize = Scene.getSize();
	const sceneWidth2 = sceneSize.x / 2;
	const sceneHeight2 = sceneSize.y / 2;

	const axeX = Math.floor(sceneWidth2 + centerPx.x);
	const axeY = Math.floor(sceneHeight2 + centerPx.y);

	return {
		x: axeX,
		y: axeY,
	};
}
